/* eslint-disable react/style-prop-object --
   The DS ui/Button exposes a variant prop literally named `style`
   (style="stroke" | ...), which this rule mistakes for the DOM style object. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  InputSearch,
  Pagination,
  showErrorToast,
  showSuccessToast,
  TableData,
} from '@/components';
import { Button } from '@/components/ui/button';
import { Column } from '@/types/common';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import type { FilterValues } from './WarehouseFilter';
import type {
  FulfillmentStatus,
  HubRef,
  OutboundOrderStatus,
  PurchaseOrder,
  PurchaseOrderReceiptStatus,
  PurchaseOrderStatus,
  PurchaseOrderType,
  SkuStatus,
} from '@/services/api/warehouse';

// Shared building blocks of the read-only Warehouse pages. Layout and labels
// follow the logistic web's WMS screens; styling is this app's DS tokens.

/** The client sees every hub at once, so pages default to a long page. */
export const DEFAULT_LIMIT = 100;
export const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

export const WAREHOUSE_PATHS = {
  stock: '/warehouse/stock',
  sku: '/warehouse/sku',
  outbound: '/warehouse/outbound',
  inbound: '/warehouse/inbound',
  purchaseOrder: '/warehouse/purchase-order',
} as const;

/* ------------------------------- format ------------------------------- */

/** `07 Oct 2026`; a bare `YYYY-MM-DD` stays that calendar day. */
export const fmtDate = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY') : '—';

export const fmtDateTime = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

export const fmtQty = (n?: number | null): string => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '0';
  return v.toLocaleString('id-ID', { maximumFractionDigits: 2 });
};

export const hubName = (hub: HubRef | null | undefined, hubID: number) =>
  hub?.name || `Hub #${hubID}`;

export const codeCls = 'font-mono text-xs font-semibold text-text-strong-950';
export const metaCls = 'text-xs text-text-soft-400';
export const nameCls = 'text-sm font-medium text-text-strong-950';

/** Hub names run long ("Dash Hub Jakarta Selatan - Kemang"): truncate in
 *  tables so 1080–1280px screens keep every column, full name on hover. */
export const HubCell: React.FC<{
  hub: HubRef | null | undefined;
  hubID: number;
}> = ({ hub, hubID }) => {
  const name = hubName(hub, hubID);
  return (
    <span className={`${nameCls} block max-w-[150px] truncate`} title={name}>
      {name}
    </span>
  );
};
export const numCls = 'text-sm tabular-nums text-text-strong-950';

/* ------------------------------- status ------------------------------- */

type Tone =
  'information' | 'warning' | 'success' | 'error' | 'feature' | 'neutral';

const TONE_CLASSES: Record<Tone, string> = {
  information: 'bg-information-lighter text-information-dark',
  warning: 'bg-warning-lighter text-warning-dark',
  success: 'bg-success-lighter text-success-dark',
  error: 'bg-error-lighter text-error-dark',
  feature: 'bg-feature-lighter text-feature-dark',
  neutral: 'bg-bg-weak-50 text-text-sub-600',
};

export interface StatusMeta {
  label: string;
  tone: Tone;
}

export const OUTBOUND_STATUS: Record<OutboundOrderStatus, StatusMeta> = {
  OPEN: { label: 'Open', tone: 'information' },
  PREPARING: { label: 'Preparing', tone: 'warning' },
  READY_TO_PICKUP: { label: 'Ready to pick up', tone: 'feature' },
  SHIPPED: { label: 'Shipped', tone: 'feature' },
  IN_DELIVERY: { label: 'In delivery', tone: 'warning' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'error' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const FULFILLMENT_STATUS: Record<FulfillmentStatus, StatusMeta> = {
  AT_HUB: { label: 'At hub', tone: 'feature' },
  DISPATCHED: { label: 'Dispatched', tone: 'feature' },
  IN_DELIVERY: { label: 'In delivery', tone: 'warning' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'error' },
  RETURNED: { label: 'Returned', tone: 'error' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const PO_STATUS: Record<PurchaseOrderStatus, StatusMeta> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  CONFIRMED: { label: 'Confirmed', tone: 'success' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const RECEIPT_STATUS: Record<PurchaseOrderReceiptStatus, StatusMeta> = {
  SCHEDULED: { label: 'Scheduled', tone: 'information' },
  PARTIAL_RECEIPT: { label: 'Partial receipt', tone: 'warning' },
  FINISHED: { label: 'Finished', tone: 'success' },
};

export const SKU_STATUS: Record<SkuStatus, StatusMeta> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  INACTIVE: { label: 'Inactive', tone: 'neutral' },
};

export const PO_TYPE_LABEL: Record<PurchaseOrderType, string> = {
  REGULAR: 'Regular',
  TRANSFER: 'Transfer',
  RETURN: 'Return',
};

export const OUTBOUND_SOURCE_LABEL: Record<string, string> = {
  MANUAL: 'Manual',
  CSV: 'CSV upload',
  CLIENT_API: 'Client API',
};

export const SERVICE_LABEL: Record<string, string> = {
  SAME_DAY: 'Same day',
  NEXT_DAY: 'Next day',
};

/** Who carried an outbound order out (set at Submit shipping). */
export const CARRIER_LABEL: Record<string, string> = {
  DASH: 'Dash',
  LION_PARCEL: 'Lion Parcel',
  HANDOVER: 'Serah terima langsung',
};

/** Product names run long ("Lampu LED 12W (box isi 10)"): wrap up to three
 *  lines inside a fixed width instead of cutting them off. */
export const productNameCls = `${nameCls} block max-w-[240px] whitespace-normal break-words line-clamp-3`;

export const METHOD_LABEL: Record<string, string> = {
  DELIVERY: 'Delivery',
  TAKE_AWAY: 'Take away',
};

/** Unknown enum values pass through as-is. */
export const labelOf = (map: Record<string, string>, key?: string | null) =>
  key ? (map[key] ?? key) : '—';

/** Where a PO's goods come from, labelled by type. */
export const poSourceOf = (
  po: PurchaseOrder
): { label: string; value: string } => {
  switch (po.type ?? 'REGULAR') {
    case 'TRANSFER':
      return {
        label: 'Hub asal',
        value: po.sourceHub?.name || po.supplierName,
      };
    case 'RETURN':
      return { label: 'Diterima dari', value: po.supplierName };
    default:
      return { label: 'Vendor', value: po.vendor?.name || po.supplierName };
  }
};

export const StatusBadge: React.FC<{
  map: Record<string, StatusMeta>;
  value?: string | null;
}> = ({ map, value }) => {
  if (!value) return <span className={metaCls}>—</span>;
  const meta = map[value] ?? { label: value, tone: 'neutral' as Tone };
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASSES[meta.tone]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {meta.label}
    </span>
  );
};

export const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-md border border-stroke-soft-200 px-2 py-0.5 text-xs font-medium text-text-sub-600">
    {children}
  </span>
);

/* -------------------------------- data -------------------------------- */

export interface LoadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Loads `fn` whenever `deps` change. A response that arrives after a newer
 * request started is dropped, so a slow page never overwrites a fast one.
 */
export const useLoad = <T,>(
  fn: () => Promise<T>,
  deps: React.DependencyList
): LoadState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);

  useEffect(() => {
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    run()
      .then((res) => {
        if (id === seq.current) setData(res);
      })
      .catch((err) => {
        if (id === seq.current) setError(getBackendErrorMessage(err));
      })
      .finally(() => {
        if (id === seq.current) setLoading(false);
      });
  }, [run, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
};

/* ------------------------------- layout ------------------------------- */

export const PageHead: React.FC<{
  title: string;
  description: string;
  /** Right-aligned header buttons (Download CSV). */
  actions?: React.ReactNode;
}> = ({ title, description, actions }) => (
  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
    <div>
      <div className="mb-2 flex items-center gap-2">
        <i className="ri-building-4-line text-sm text-primary" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-soft-400">
          Warehouse
        </span>
      </div>
      <h1 className="mb-2 text-2xl font-bold tracking-tight text-text-strong-950 sm:text-3xl">
        {title}
      </h1>
      <p className="max-w-2xl text-sm text-text-sub-600">{description}</p>
    </div>
    {actions && (
      <div className="flex flex-shrink-0 flex-wrap gap-3">{actions}</div>
    )}
  </div>
);

/**
 * "Download CSV" of a list as filtered right now (its tab + search). The
 * server builds the file; a failure (e.g. too many rows) is toasted.
 */
export const ExportCsvButton: React.FC<{
  what: string;
  onExport: () => Promise<void>;
}> = ({ what, onExport }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onExport();
      showSuccessToast('CSV di-download', `${what} berhasil di-download.`);
    } catch (err) {
      showErrorToast(
        `CSV ${what} gagal di-download`,
        err instanceof Error ? err.message : String(err)
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      tone="neutral"
      style="stroke"
      loading={busy}
      onClick={run}
      leftIcon={<i className="ri-download-2-line text-[14px]" />}
    >
      Download CSV
    </Button>
  );
};

export const StatTile: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-5 py-4 shadow-card-xs">
    <div className="text-[13px] font-medium text-text-sub-600">{label}</div>
    <div className="mt-1.5 text-2xl font-bold tracking-tight text-text-strong-950 tabular-nums">
      {value}
    </div>
  </div>
);

export const LoadError: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
    <i className="ri-error-warning-line text-3xl text-error-base" />
    <p className="text-sm text-text-sub-600">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="rounded-lg border border-stroke-soft-200 px-3 py-1.5 text-sm font-medium text-text-sub-600 transition-colors hover:bg-bg-weak-50"
    >
      Coba lagi
    </button>
  </div>
);

export const EmptyState: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex flex-col items-center gap-2 py-12 text-center">
    <i className="ri-inbox-line text-3xl text-text-soft-400" />
    <p className="text-sm text-text-sub-600">{title}</p>
  </div>
);

export interface ListTab<K extends string> {
  key: K;
  label: string;
  count: number | null;
}

/**
 * The table card of every list: optional status tabs, search, table and
 * pagination — the logistic web's ListShell in this app's styling.
 */
export const ListCard = <T extends Record<string, any>, K extends string>({
  tabs,
  activeTab,
  onTabChange,
  searchPlaceholder,
  onSearch,
  filter,
  columns,
  rows,
  loading,
  error,
  onRetry,
  emptyTitle,
  onRowClick,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
}: {
  tabs?: ListTab<K>[];
  activeTab?: K;
  onTabChange?: (key: K) => void;
  searchPlaceholder: string;
  onSearch: (value: string) => void;
  /** The list's Filter button (WarehouseFilter), right of the search. */
  filter?: React.ReactNode;
  columns: Column<T>[];
  rows: T[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  emptyTitle: string;
  onRowClick?: (row: T) => void;
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}) => (
  <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
    {tabs && (
      <div className="flex items-center gap-1 overflow-x-auto border-b border-stroke-soft-200 px-4 pt-1">
        {tabs.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onTabChange?.(tab.key)}
              className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-3 text-sm font-medium transition-colors focus:outline-none ${
                isActive
                  ? 'text-primary'
                  : 'text-text-sub-600 hover:text-text-strong-950'
              }`}
            >
              {tab.label}
              <span
                className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                  isActive
                    ? 'bg-primary text-static-white'
                    : 'bg-bg-weak-50 text-text-sub-600'
                }`}
              >
                {tab.count ?? '—'}
              </span>
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-primary" />
              )}
            </button>
          );
        })}
      </div>
    )}

    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 p-4">
      <InputSearch
        className="min-w-[200px] max-w-[380px] flex-1"
        placeholder={searchPlaceholder}
        onSearch={onSearch}
        onClear={() => onSearch('')}
      />
      {filter}
    </div>

    {error ? (
      <LoadError message={error} onRetry={onRetry} />
    ) : (
      <>
        <TableData<T>
          data={rows}
          columns={columns}
          loading={loading}
          size="small"
          className="rounded-none border-0 shadow-none"
          onRowClick={onRowClick ? (row) => onRowClick(row) : undefined}
          emptyState={<EmptyState title={emptyTitle} />}
        />
        {total > 0 && (
          <div className="px-4 pb-4">
            <Pagination
              currentPage={page}
              totalItems={total}
              itemsPerPage={limit}
              onPageChange={onPageChange}
              onPageSizeChange={onLimitChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </div>
        )}
      </>
    )}
  </div>
);

/** tab + search + filters + page + page size of a list; a new tab,
 *  search, filter or size restarts at page 1. */
export const useListState = <K extends string>(initialTab: K) => {
  const [tab, setTabState] = useState<K>(initialTab);
  const [search, setSearchState] = useState('');
  const [filters, setFiltersState] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [limit, setLimitState] = useState(DEFAULT_LIMIT);
  return {
    tab,
    search,
    filters,
    /** Stable string of the applied filters — for load dependencies. */
    filterKey: JSON.stringify(filters),
    page,
    limit,
    setPage,
    setFilters: (next: FilterValues) => {
      setFiltersState(next);
      setPage(1);
    },
    setTab: (next: K) => {
      setTabState(next);
      setPage(1);
    },
    setSearch: (next: string) => {
      setSearchState(next.trim());
      setPage(1);
    },
    setLimit: (next: number) => {
      setLimitState(next);
      setPage(1);
    },
  };
};

/* ------------------------------- detail ------------------------------- */

export const DetailHead: React.FC<{
  backTo: string;
  backLabel: string;
  title: string;
  badges?: React.ReactNode;
  meta?: React.ReactNode;
}> = ({ backTo, backLabel, title, badges, meta }) => (
  <div>
    <Link
      to={backTo}
      className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-text-sub-600 transition-colors hover:text-primary"
    >
      <i className="ri-arrow-left-line" />
      {backLabel}
    </Link>
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="font-mono text-2xl font-bold tracking-tight text-text-strong-950">
        {title}
      </h1>
      {badges}
    </div>
    {meta && <p className="mt-1 text-sm text-text-soft-400">{meta}</p>}
  </div>
);

export const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  flush?: boolean;
}> = ({ title, children, flush }) => (
  <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
    <div className="border-b border-stroke-soft-200 px-5 py-3">
      <h2 className="text-sm font-semibold text-text-strong-950">{title}</h2>
    </div>
    <div className={flush ? '' : 'space-y-3 px-5 py-4'}>{children}</div>
  </div>
);

/** One label / value pair; an empty value shows an em-dash. */
export const KV: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}> = ({ label, value, sub }) => (
  <div>
    <p className="text-xs font-medium text-text-soft-400">{label}</p>
    <div className="mt-0.5 break-words text-sm text-text-strong-950">
      {value === null || value === undefined || value === '' ? '—' : value}
    </div>
    {sub && <p className={metaCls}>{sub}</p>}
  </div>
);

export const KVGrid: React.FC<{ items: [string, React.ReactNode][] }> = ({
  items,
}) => (
  <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
    {items.map(([label, value]) => (
      <KV key={label} label={label} value={value} />
    ))}
  </div>
);

export const Notice: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="rounded-xl border border-warning-base/40 bg-warning-lighter px-4 py-3">
    <p className="text-sm font-semibold text-warning-dark">{title}</p>
    <p className="mt-0.5 text-sm text-text-sub-600">{children}</p>
  </div>
);

export interface TimelineEntry {
  id: string;
  at: string;
  title: string;
  note?: string;
  badge?: React.ReactNode;
}

/** Newest first. */
export const Timeline: React.FC<{ entries: TimelineEntry[] }> = ({
  entries,
}) =>
  entries.length === 0 ? (
    <p className={metaCls}>Belum ada riwayat.</p>
  ) : (
    <ol className="space-y-4">
      {entries.map((e, i) => (
        <li key={e.id} className="relative flex gap-3">
          <div className="flex flex-col items-center">
            <span
              className={`mt-1.5 h-2.5 w-2.5 rounded-full ${
                i === 0 ? 'bg-primary' : 'bg-stroke-sub-300'
              }`}
            />
            {i < entries.length - 1 && (
              <span className="mt-1 w-px flex-1 bg-stroke-soft-200" />
            )}
          </div>
          <div className="min-w-0 flex-1 pb-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-text-strong-950">
                {e.title}
              </p>
              {e.badge}
            </div>
            {e.note && (
              <p className="mt-0.5 text-sm text-text-sub-600">{e.note}</p>
            )}
            <p className="mt-0.5 text-xs text-text-soft-400">
              {fmtDateTime(e.at)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );

export const DetailSkeleton: React.FC = () => (
  <div className="space-y-5 p-4 sm:p-6">
    <div className="h-6 w-40 animate-pulse rounded bg-bg-soft-200" />
    <div className="h-9 w-72 animate-pulse rounded bg-bg-soft-200" />
    <div className="h-64 animate-pulse rounded-xl bg-bg-soft-200" />
  </div>
);
